import { FC, useRef, useState } from 'react';
import cx from 'classnames';
import { ClickAwayListener, Popper } from '@material-ui/core';
import css from './EnhancedPractitionerDropdown.module.scss';
import { IPractitionerOptions } from 'pages/ClinicDetailsPage/BookNowPanel/SingleBooking/SingleBooking';
import { ReactComponent as DropdownIcon } from 'assets/icons/arrow-down.svg';
import NoAvatarSrc from 'assets/images/no-avatar.svg';
import { filterSpecialties } from 'utils/common';

interface EnhancedPractitionerDropdownProps {
  currentPractitioner: { id: string; title: string };
  currentDentist: { id: string; title: string };
  practitionerOptions: IPractitionerOptions[];
  dentistOptions: IPractitionerOptions[];
  onChange: (isHygienistTab: boolean, practitionerId: string) => void;
  isPopperFullWidth?: boolean;
}

const EnhancedPractitionerDropdown: FC<EnhancedPractitionerDropdownProps> = ({
  practitionerOptions,
  dentistOptions,
  currentDentist,
  currentPractitioner,
  onChange,
  isPopperFullWidth = false,
}) => {
  const popperId = 'practitioner-popper';

  const noPreferenceId = 'all';

  const [isOpen, setIsOpen] = useState(false);
  const anchorElRef = useRef<HTMLButtonElement | null>(null);
  const [isHygienistTab, setIsHygienistTab] = useState(true);
  const [anchorWidth, setAnchorWidth] = useState<number>();

  const openPopper = () => {
    // Matching the popper to its anchor keeps it inside narrow containers
    // such as a family booking row or the embedded widget iframe.
    if (isPopperFullWidth) {
      setAnchorWidth(anchorElRef.current?.offsetWidth);
    }

    setIsOpen(true);
  };

  const buttonClassname = cx({
    [css['button']]: true,
    [css['button--active']]: isOpen,
  });

  const iconClassName = cx({
    [css['popper-icon']]: true,
    [css['popper-icon-open']]: isOpen,
  });

  const popperContainerClassName = cx({
    [css['popper-container']]: true,
    [css['popper-container-full-width']]: isPopperFullWidth,
  });

  const popperNameClassName = cx({
    [css['popper-name']]: true,
    [css['popper-name-blur']]: isOpen,
  });

  const options = isHygienistTab ? practitionerOptions : dentistOptions;

  const onChangePractitioner = (id: string) => {
    setIsOpen(false);
    onChange(isHygienistTab, id);
  };

  const displayPractitionerName = () => {
    if (currentPractitioner.id === 'all' && currentDentist.id === 'all') {
      return 'Select Your Providers';
    } else {
      return `${currentPractitioner.title}, ${currentDentist.title}`;
    }
  };

  return (
    <div className={css['container']}>
      <h4 className={css['title']}>Practitioner</h4>
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <div>
          <button
            className={buttonClassname}
            aria-describedby={popperId}
            type="button"
            ref={anchorElRef}
            onClick={() => {
              // Prevents the popper from closing when clicking the button
              if (!isOpen) return openPopper();
            }}
          >
            <span className={popperNameClassName}>
              {displayPractitionerName()}
            </span>
            <DropdownIcon
              className={iconClassName}
              onClick={() => (isOpen ? setIsOpen(false) : openPopper())}
            />
          </button>
          <Popper
            id={popperId}
            open={isOpen}
            anchorEl={anchorElRef.current}
            placement="bottom-start"
            className={popperContainerClassName}
            style={isPopperFullWidth ? { width: anchorWidth } : undefined}
          >
            <div className={css['popper']}>
              <div className={css['switch']}>
                <button
                  className={`${css['switch-option']} ${
                    isHygienistTab ? css['switch-option-active'] : ''
                  }`}
                  onClick={() => setIsHygienistTab(true)}
                >
                  Hygienist
                </button>
                <button
                  className={
                    css['switch-option'] +
                    ` ${!isHygienistTab ? css['switch-option-active'] : ''}
                  ${
                    dentistOptions.length === 0
                      ? css['switch-option-disabled']
                      : ''
                  }`
                  }
                  onClick={() => setIsHygienistTab(false)}
                >
                  Doctor
                </button>
              </div>
              <div className={css['main-section']}>
                <button
                  className={`${css['main-section-item']} ${
                    (
                      isHygienistTab
                        ? currentPractitioner.id === noPreferenceId
                        : currentDentist.id === noPreferenceId
                    )
                      ? css['main-section-item-active']
                      : ''
                  }`}
                  onClick={() => onChangePractitioner(noPreferenceId)}
                >
                  No Preference
                </button>

                {options.map((option) => (
                  <button
                    key={option.id}
                    className={`${css['main-section-item']} ${
                      (
                        isHygienistTab
                          ? currentPractitioner.id === option.id
                          : currentDentist.id === option.id
                      )
                        ? css['main-section-item-active']
                        : ''
                    }`}
                    onClick={() => onChangePractitioner(option.id)}
                  >
                    <div className={`${css['main-section-doctor-info']}`}>
                      <img
                        src={option.avatar || NoAvatarSrc}
                        alt={`${option.title}__avatar`}
                        className={css['practitioner-avatar']}
                      />
                      <div className={css['practitioner-info']}>
                        <div className={css['bold']}>
                          <span>{option.title}</span>
                        </div>
                        <span>
                          {filterSpecialties(option.specialties)
                            .map((specialty) => specialty.name)
                            .join(', ')}
                        </span>
                      </div>
                    </div>

                    {option.insurance && (
                      <div>{`${option.insurance.isInNetwork ? 'In network' : 'Out of network'}`}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default EnhancedPractitionerDropdown;
